import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Upload, Download, FileText, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CasaMadreCsvImport() {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [dryReport, setDryReport] = useState(null);
  const [applyReport, setApplyReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("tk_token");
      const res = await fetch(`${API}/csv/template`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "template_catalogo.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download template");
    }
  };

  const runDry = async () => {
    if (!file) { toast.error("Seleziona prima un CSV"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const { data } = await api.post("/csv/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDryReport(data);
      setApplyReport(null);
      toast.success("Anteprima generata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!file) return;
    if (!window.confirm("Confermi l'applicazione delle modifiche al catalogo?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const { data } = await api.post("/csv/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setApplyReport(data);
      toast.success("Import completato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10 max-w-4xl" data-testid="csv-import-page">
        <div className="mb-10">
          <div className="overline text-brand mb-2">Casa Madre · Strumenti</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Importazione CSV catalogo</h1>
          <p className="text-muted-foreground mt-2 text-sm">Carica o aggiorna massivamente prodotti e varianti. Il template è generato dinamicamente in base al vertical della tua network.</p>
        </div>

        <div className="bg-white border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-bold text-lg">1. Scarica il template</h3>
              <p className="text-xs text-muted-foreground mt-1">Include tutti i campi base + campi custom vertical della tua network</p>
            </div>
            <button data-testid="csv-download-template" onClick={downloadTemplate}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors">
              <Download className="w-4 h-4" /> Scarica template CSV
            </button>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-6 mb-6">
          <h3 className="font-heading font-bold text-lg mb-3">2. Carica il tuo CSV compilato</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0]); setDryReport(null); setApplyReport(null); }}
              data-testid="csv-file-input" className="hidden" />
            <button onClick={() => fileRef.current?.click()} data-testid="csv-pick-file"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-sm border border-border text-sm hover:bg-accent transition-colors">
              <Upload className="w-4 h-4" /> {file ? file.name : "Seleziona CSV"}
            </button>
            <button onClick={runDry} disabled={!file || busy} data-testid="csv-dry-btn"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Anteprima (dry-run)
            </button>
          </div>
        </div>

        {dryReport && (
          <div className="bg-white border border-border rounded-sm p-6 mb-6" data-testid="csv-dry-report">
            <h3 className="font-heading font-bold text-lg mb-3">3. Anteprima modifiche</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Stat label="Righe totali" v={dryReport.summary.total_rows} />
              <Stat label="Nuovi prodotti" v={dryReport.summary.to_create_products} color="emerald" />
              <Stat label="Aggiorn. prodotti" v={dryReport.summary.to_update_products} color="blue" />
              <Stat label="Nuove varianti" v={dryReport.summary.to_create_variants} color="emerald" />
              <Stat label="Errori" v={dryReport.summary.errors.length} color="red" />
            </div>
            {dryReport.summary.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
                <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-2">
                  <AlertCircle className="w-4 h-4" /> Errori riscontrati
                </div>
                <ul className="text-xs text-red-700 space-y-0.5 font-mono">
                  {dryReport.summary.errors.slice(0, 10).map((e, i) => <li key={i}>Riga {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
            <button onClick={runApply} data-testid="csv-apply-btn"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" /> Applica modifiche
            </button>
          </div>
        )}

        {applyReport && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6" data-testid="csv-apply-report">
            <div className="flex items-center gap-2 text-emerald-800 font-medium mb-3">
              <CheckCircle2 className="w-5 h-5" /> Import completato
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Prodotti creati" v={applyReport.summary.created_products} color="emerald" />
              <Stat label="Prodotti agg." v={applyReport.summary.updated_products} color="blue" />
              <Stat label="Varianti create" v={applyReport.summary.created_variants} color="emerald" />
              <Stat label="Varianti agg." v={applyReport.summary.updated_variants} color="blue" />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const Stat = ({ label, v, color = "neutral" }) => (
  <div className={`px-3 py-2 rounded-sm border border-border bg-${color === "neutral" ? "white" : color + "-50"}`}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`font-heading font-bold text-2xl ${color === "red" ? "text-red-700" : color === "emerald" ? "text-emerald-700" : color === "blue" ? "text-blue-700" : ""}`}>{v}</div>
  </div>
);
